import { useAtomValue } from 'jotai';
import { useMatch } from 'react-router-dom';
import { exportContentAtom } from '@/atoms/exporting';
import { comparisonPlayerInfoAtom, comparisonTeamInfoAtom } from '@/atoms/exporting/comparisonExportInfo';
import { selectedCompetitionsAtom } from '@/atoms/filters/highLevel/competitions';
import { selectedSeasonsAtom } from '@/atoms/filters/highLevel/seasons';
import {
  competitionAverageSelectedCompetitionsAtom as playerCompetitionAverageSelectedCompetitionsAtom,
  competitionAverageSelectedSeasonsAtom as playerCompetitionAverageSelectedSeasonsAtom,
} from '@/atoms/filters/player/playerCompetitionAverageFilters';
import {
  competitionAverageSelectedCompetitionsAtom as teamCompetitionAverageSelectedCompetitionsAtom,
  competitionAverageSelectedSeasonsAtom as teamCompetitionAverageSelectedSeasonsAtom,
} from '@/atoms/filters/team/teamCompetitionAverageFilters';
import { unwrappedSelectedPlayerNameAtom } from '@/atoms/player/info';
import { unwrappedPlayerComparisonDisplayNameAtom } from '@/atoms/queries/player/playerComparisonInfo';
import { unwrappedComparisonTeamNameAtom } from '@/atoms/queries/team/teamComparison';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { unwrappedSelectedTeamNameAtom } from '@/atoms/team/info';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { ExportContent } from '@/types/exporting';
import { useExportInfo } from './useExportInfo';
import { useExportVisInfo } from './useExportVisInfo';

export const useComparisonExportInfo = () => {
  const exportContent = useAtomValue(exportContentAtom);
  const isVisExportOnly = exportContent === ExportContent.VIS;
  const isPlayerSection = useMatch('/:locale/player/*');
  const isRadarPage = !!useMatch('/:locale/:entity/:entityId/radar/*');
  const comparisonId = useAtomValue(comparisonIdAtom);
  const primaryPlayerName = useAtomValue(unwrappedSelectedPlayerNameAtom);
  const primaryTeamName = useAtomValue(unwrappedSelectedTeamNameAtom);
  const comparisonPlayerName = useAtomValue(unwrappedPlayerComparisonDisplayNameAtom);
  const comparisonTeamName = useAtomValue(unwrappedComparisonTeamNameAtom);
  const primaryEntityName = isPlayerSection ? primaryPlayerName : primaryTeamName;
  const comparisonEntityName = isPlayerSection ? comparisonPlayerName : comparisonTeamName;
  const comparisonPlayerInfo = useAtomValue(comparisonPlayerInfoAtom);
  const comparisonTeamInfo = useAtomValue(comparisonTeamInfoAtom);
  const selectedCompetitions = useAtomValue(selectedCompetitionsAtom);
  const selectedSeasons = useAtomValue(selectedSeasonsAtom);
  const isCompetitionAverageMode = useIsCompetitionAverageMode();
  const playerCompetitionAverageCompetitions = useAtomValue(playerCompetitionAverageSelectedCompetitionsAtom);
  const teamCompetitionAverageCompetitions = useAtomValue(teamCompetitionAverageSelectedCompetitionsAtom);
  const playerCompetitionAverageSeasons = useAtomValue(playerCompetitionAverageSelectedSeasonsAtom);
  const teamCompetitionAverageSeasons = useAtomValue(teamCompetitionAverageSelectedSeasonsAtom);
  const { info1, info2, info3 } = useExportVisInfo();

  const playerTeamInfo = useExportInfo(comparisonPlayerInfo?.teamOptions ?? [], 'Teams', 2);
  const comparisonPlayerCompetitionInfo = useExportInfo(
    comparisonPlayerInfo?.selectedCompetitions ?? [],
    'Competitions',
    1,
  );
  const comparisonPlayerSeasonInfo = useExportInfo(comparisonPlayerInfo?.selectedSeasons ?? [], 'Seasons', 3, true);
  const comparisonTeamCompetitionInfo = useExportInfo(
    comparisonTeamInfo?.selectedCompetitions ?? [],
    'Competitions',
    1,
  );
  const comparisonTeamSeasonInfo = useExportInfo(comparisonTeamInfo?.selectedSeasons ?? [], 'Seasons', 3, true);
  const competitionInfo = useExportInfo(selectedCompetitions, 'Competitions', 1);
  const seasonInfo = useExportInfo(selectedSeasons, 'Seasons', 3, true);

  const competitionAverageCompetition = isPlayerSection
    ? playerCompetitionAverageCompetitions[0]
    : teamCompetitionAverageCompetitions[0];
  const competitionAverageSeasons = isPlayerSection ? playerCompetitionAverageSeasons : teamCompetitionAverageSeasons;
  const competitionAverageSeasonInfo = useExportInfo(competitionAverageSeasons, 'Seasons', 3, true);

  const comparisonEntityCompetitionInfo = isPlayerSection
    ? comparisonPlayerCompetitionInfo
    : comparisonTeamCompetitionInfo;
  const comparisonEntitySeasonInfo = isPlayerSection ? comparisonPlayerSeasonInfo : comparisonTeamSeasonInfo;

  const isComparisonActive = !!comparisonId || isCompetitionAverageMode;
  const isComparisonRadar = isRadarPage && isVisExportOnly && isComparisonActive;
  const showVisInfo = !isComparisonRadar;
  const showSubtitles = showVisInfo && (!isRadarPage || isVisExportOnly);

  const radarSub1 = isRadarPage && isPlayerSection ? playerTeamInfo : info1;
  const radarSub2 = isRadarPage && isPlayerSection ? competitionInfo : info2;
  const radarSub3 = isRadarPage && isPlayerSection ? seasonInfo : info3;

  const comparisonTitle = isCompetitionAverageMode ? competitionAverageCompetition?.label ?? '' : comparisonEntityName;
  const comparisonCountryCode = isCompetitionAverageMode
    ? competitionAverageCompetition?.optional?.countryCode ?? null
    : null;
  const comparisonCompetitionInfo = isCompetitionAverageMode ? '' : comparisonEntityCompetitionInfo;
  const comparisonSeasonInfo = isCompetitionAverageMode ? competitionAverageSeasonInfo : comparisonEntitySeasonInfo;
  const comparisonEntityTeamInfo = !isCompetitionAverageMode && isPlayerSection ? comparisonPlayerInfo?.team ?? '' : '';

  return {
    isRadarPage,
    showVisInfo,
    exportTitle: isComparisonRadar ? primaryEntityName : '',
    comparisonTitle: isComparisonRadar ? comparisonTitle : '',
    comparisonCountryCode: isComparisonRadar ? comparisonCountryCode : null,
    primaryTeamInfo: isComparisonRadar && isPlayerSection ? playerTeamInfo : '',
    comparisonTeamInfo: isComparisonRadar ? comparisonEntityTeamInfo : '',
    comparisonInfo1: isComparisonRadar ? comparisonCompetitionInfo : '',
    comparisonInfo2: isComparisonRadar ? comparisonSeasonInfo : '',
    exportInfo1: competitionInfo,
    exportInfo2: seasonInfo,
    firstSubtitle: showSubtitles ? radarSub1 : '',
    secondSubtitle: showSubtitles ? radarSub2 : '',
    thirdSubtitle: showSubtitles ? radarSub3 : '',
  };
};
